<script setup name="TvbHomeSmallBannerTemplateTwo">
import { ref, inject } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { FreeMode } from 'swiper/modules'
import { action } from './../../../../../../utils/gtm'
import { checkVideo } from '@/utils'

const swiperRef = ref()
const size = inject('size')

const props = defineProps({
  info: {
    type: Object,
    default: null
  },
  list: {
    type: Array,
    default: () => []
  }
})

const offsetMap = {
  s: 16,
  xs: 12
}
const slidePrev = () => swiperRef?.value?.$el?.swiper?.slidePrev()
const slideNext = () => swiperRef?.value?.$el?.swiper?.slideNext()
const tracking = (item) => {
  action({
    page_title: props.info.title,
    screen_class: item.title || item.name,
    screenID: 'S001',
    eventCategory: 'home_banner',
    eventLabel: props.info.api,
    eventID: item.id || item.post_id,
    screenLabel: item.jump_url || item.link
  })
}

defineExpose({ slidePrev, slideNext })
</script>

<template>
  <main class="tvb-home-small-banner-main" :class="[size]">
    <swiper
      v-if="list.length"
      :key="size"
      :speed="800"
      class="tvb-home-small-banner-swiper"
      :slides-per-view="['s', 'xs'].includes(size) ? 2.3529 : 6"
      :slides-per-group="['s', 'xs'].includes(size) ? 1 : 6"
      :space-between="['s', 'xs'].includes(size) ? 10 : 16"
      :slides-offset-after="offsetMap[size]"
      :slides-offset-before="offsetMap[size]"
      :loop="!['s', 'xs'].includes(size) && 6 < list.length"
      :modules="[FreeMode]"
      :free-mode="{
        enabled: ['s', 'xs'].includes(size),
        momentum: false,
        minimumVelocity: 10,
        momentumBounceRatio: 0.01,
        momentumRatio: 0.01,
        momentumVelocityRatio: 0.01
      }"
      ref="swiperRef"
    >
      <swiper-slide
        class="tvb-home-small-banner-swiper-slide"
        v-for="(item, index) in list"
        :key="index"
      >
        <tvb-router-link
          :to="item.jump_url"
          :link-rel="item.link_rel"
          :link-target="item.link_target"
          class="tvb-home-small-banner-card"
          @click="tracking(item)"
        >
          <tvb-image
            class="tvb-home-small-banner-card-image"
            border
            :border-radius="['s', 'xs'].includes(size) ? 12 : 16"
            :play-icon="{ size: { xl: 48, l: 48, m: 48, s: 32, xs: 32 } }"
            :src="item.image"
            :icon="item.icon"
            :icon-text="item.icon_text"
            :alt="item.image_alt_text || item.title"
            :is-video="checkVideo(item)"
            transform
          />
          <div class="tvb-home-small-banner-card-titles">
            <h3 class="tvb-home-small-banner-card-title" v-if="item.title" :title="item.title">
              <span>{{ item.title }}</span>
            </h3>
            <h4
              class="tvb-home-small-banner-card-subtitle"
              v-if="item.subtitle"
              :title="item.subtitle"
            >
              <span>{{ item.subtitle }}</span>
            </h4>
          </div>
          <small
            class="tvb-home-small-banner-card-description"
            v-if="item.description"
            :title="item.description"
          >
            {{ item.description }}
          </small>
        </tvb-router-link>
      </swiper-slide>
    </swiper>
  </main>
</template>

<style lang="scss" scoped>
.tvb-home-small-banner-main {
  margin-top: 16px;

  .tvb-home-small-banner-card {
    text-decoration: none;
    position: relative;

    .tvb-home-small-banner-card-icon {
      height: 32px;
      width: 32px;
      position: absolute;
      top: 0;
      right: 0;
      z-index: 9;
      object-fit: cover;
      border-radius: 0 16px 0 0;
      overflow: hidden;
      @include safari-overflow-hidden();
    }

    .tvb-home-small-banner-card-image {
      width: 100%;
      aspect-ratio: 70/109;
      border-radius: 16px;
      overflow: hidden;
      --tvb-hairline-border-radius: calc(2 * 16px);
      @include safari-overflow-hidden();
    }

    .tvb-home-small-banner-card-titles {
      .tvb-home-small-banner-card-title,
      .tvb-home-small-banner-card-subtitle {
        font-size: 18px;
        font-weight: 500;
        color: #231f20;
        line-height: 26px;
        word-break: break-all;
        letter-spacing: 0.5px;
        @include line-text-ellipsis(1);
        span {
          @include text-hover-before();
          &:hover {
            @include text-hover-active();
          }
        }
      }
      .tvb-home-small-banner-card-subtitle {
        font-weight: 400;
        color: #444;
        margin-top: 2px;
      }
    }

    .tvb-home-small-banner-card-title {
      margin-top: 10px;
      // &:nth-last-child(-n + 2) {
      //   @include line-text-ellipsis(2);
      // }
    }
    .tvb-home-small-banner-card-description {
      margin-top: 4px;
      font-size: 16px;
      font-weight: 400;
      color: #818d95;
      line-height: 22px;
      letter-spacing: 0.5px;
      @include line-text-ellipsis(1);
    }
  }

  &.s,
  &.xs {
    margin-top: 10px;
    .tvb-home-small-banner-card {
      .tvb-home-small-banner-card-icon {
        max-width: 24px;
        max-height: 24px;
        border-radius: 0 12px 0 0;
      }

      .tvb-home-small-banner-card-image {
        border-radius: 12px;
        aspect-ratio: 0.6808510638297872 /1;
        @supports not (aspect-ratio: 0.6808510638297872 /1) {
          height: 235px;
        }
      }

      .tvb-home-small-banner-card-title {
        margin-top: 6px;
        font-size: 16px;
        font-weight: 400;
        color: #231f20;
        line-height: 24px;
      }
      .tvb-home-small-banner-card-subtitle {
        margin-top: 0;
        font-size: 15px;
        font-weight: 400;
        color: #444;
        line-height: 22px;
        letter-spacing: 0.3px;
      }

      .tvb-home-small-banner-card-description {
        font-size: 12px;
        font-weight: 400;
        color: #818d95;
        line-height: 18px;
        letter-spacing: 0px;
        margin-top: 2px;
      }
    }
  }
}
</style>
